import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card, Table, Tag, Typography, EmptyState, Input, Select, Space, Badge, Button, Tooltip, theme, Segmented, Descriptions, message,
} from '../../ui';
import {
  Search, RefreshCw, Clock, AlertCircle, Plus, LayoutGrid, List,
} from 'lucide-react';
import { request } from '../../api/client';
import { usePermissionChecker, useAnyPermission } from '../../store/auth';
import { brand } from '../../assets/brand';
import { formatIst, formatIstDate, formatIstTime } from '../../lib/time';
import { ComplaintFormModal } from './ComplaintFormModal';

// Active (workflow) columns shown on the kanban. CLOSED is terminal and lives
// in the table below the board.
const COLUMNS: { key: string; title: string; color: string; tone: string }[] = [
  { key: 'OPEN', title: 'Open', color: '#1677ff', tone: '#E6F4FF' },
  { key: 'IN_PROGRESS', title: 'In Progress', color: '#E7B22C', tone: '#FFF7E0' },
  { key: 'ESCALATED', title: 'Escalated', color: '#D9363E', tone: '#FFEDED' },
  { key: 'REOPENED', title: 'Reopened', color: '#722ED1', tone: '#F4ECFF' },
  { key: 'RESOLVED', title: 'Resolved', color: brand.primary, tone: '#EAF6EE' },
];
// Subtle per-status row background for the list view (reuses the kanban tones).
const STATUS_ROW_TONE: Record<string, string> = {
  ...Object.fromEntries(COLUMNS.map((c) => [c.key, c.tone])),
  CLOSED: '#FAFAFA',
};
const SEVERITY_COLOR: Record<string, string> = { LOW: 'blue', MED: 'gold', HIGH: 'gold', CRIT: 'red' };
const SEVERITY_OPTIONS = [
  { value: 'LOW', label: 'Low' },
  { value: 'MED', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
  { value: 'CRIT', label: 'Critical' },
];
const STATUS_OPTIONS = [
  { value: 'OPEN', label: 'Open' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'ESCALATED', label: 'Escalated' },
  { value: 'REOPENED', label: 'Reopened' },
  { value: 'RESOLVED', label: 'Resolved' },
  { value: 'CLOSED', label: 'Closed' },
];
const STATUS_TAG: Record<string, string> = {
  OPEN: 'blue', IN_PROGRESS: 'gold', ESCALATED: 'red', REOPENED: 'purple', RESOLVED: 'green', CLOSED: 'neutral',
};
const STATUS_LABEL: Record<string, string> = Object.fromEntries(STATUS_OPTIONS.map((o) => [o.value, o.label]));
// Mirrors the backend COMPLAINT_TRANSITIONS — only valid next states are offered, so a
// new (OPEN) complaint must go through In Progress before it can be Resolved.
const TRANSITIONS: Record<string, string[]> = {
  OPEN: ['IN_PROGRESS', 'ESCALATED'],
  IN_PROGRESS: ['OPEN', 'ESCALATED', 'RESOLVED'],
  ESCALATED: ['IN_PROGRESS', 'RESOLVED'],
  RESOLVED: ['CLOSED', 'REOPENED'],
  REOPENED: ['IN_PROGRESS', 'ESCALATED'],
  CLOSED: ['REOPENED'],
};
const SEV_ORDER: Record<string, number> = { LOW: 0, MED: 1, HIGH: 2, CRIT: 3 };
// Most-urgent first: soonest SLA (a past date = breached) on top, no-SLA last.
const bySla = (a: any, b: any) => {
  const t = (x: any) => (x.slaDueAt ? new Date(x.slaDueAt).getTime() : Infinity);
  return t(a) - t(b);
};

export function ComplaintsBoardPage() {
  const { token } = theme.useToken();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [severity, setSeverity] = useState('');
  const [status, setStatus] = useState('');
  const [category, setCategory] = useState('');
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [hoverCol, setHoverCol] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [view, setView] = useState<'board' | 'list'>('list');
  // Only staff who can manage complaints may drag cards to change status;
  // customers get a read-only board (the /status endpoint would 403 anyway).
  const hasPerm = usePermissionChecker();
  const canManage = useAnyPermission(['COMPLAINT.ASSIGN', 'COMPLAINT.RESOLVE', 'COMPLAINT.ESCALATE']);
  const canCreate = hasPerm('COMPLAINT.CREATE');

  const { data, isFetching, refetch } = useQuery({
    queryKey: ['complaints'],
    queryFn: () => request<any[]>({ url: '/complaints' }),
  });

  const move = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      request({ url: `/complaints/${id}/status`, method: 'PATCH', data: { status } }),
    onMutate: async ({ id, status }) => {
      await qc.cancelQueries({ queryKey: ['complaints'] });
      const prev = qc.getQueryData<any[]>(['complaints']);
      qc.setQueryData<any[]>(['complaints'], (rows) =>
        (rows ?? []).map((r) => (r.id === id ? { ...r, status } : r)),
      );
      return { prev };
    },
    onError: (e: any, _v, ctx) => {
      qc.setQueryData(['complaints'], ctx?.prev);
      message.error(e.response?.data?.error?.message ?? 'Could not change status');
    },
    onSuccess: () => message.success('Status updated'),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['complaints'] });
      qc.invalidateQueries({ queryKey: ['badges'] }); // navbar open-complaints count
    },
  });

  const categories = useMemo(() => {
    const set = new Set<string>();
    for (const c of data ?? []) if (c.category) set.add(c.category);
    return Array.from(set).sort();
  }, [data]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data ?? []).filter((c) => {
      if (severity && c.severity !== severity) return false;
      if (status && c.status !== status) return false;
      if (category && c.category !== category) return false;
      if (!q) return true;
      return (
        (c.complaintNo ?? '').toLowerCase().includes(q) ||
        (c.description ?? '').toLowerCase().includes(q) ||
        (c.category ?? '').toLowerCase().includes(q)
      );
    });
  }, [data, search, severity, status, category]);

  const activeRows = useMemo(() => filtered.filter((c) => c.status !== 'CLOSED'), [filtered]);
  const closedRows = useMemo(() => filtered.filter((c) => c.status === 'CLOSED'), [filtered]);
  // List view: with no Status filter selected, hide Closed (show only non-closed). Closed
  // complaints appear only when "Closed" is explicitly chosen in the Status filter.
  const listRows = useMemo(() => (status ? filtered : activeRows), [status, filtered, activeRows]);
  const grouped = useMemo(
    () =>
      activeRows.reduce<Record<string, any[]>>((acc, c) => {
        (acc[c.status] = acc[c.status] || []).push(c);
        return acc;
      }, {}),
    [activeRows],
  );

  const onDragStart = (e: React.DragEvent, id: string) => {
    setDraggingId(id);
    e.dataTransfer.setData('text/plain', id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const onDrop = (e: React.DragEvent, status: string) => {
    e.preventDefault();
    const id = e.dataTransfer.getData('text/plain') || draggingId;
    setDraggingId(null);
    setHoverCol(null);
    if (!id) return;
    const card = (data ?? []).find((c) => c.id === id);
    if (!card || card.status === status) return;
    // Validate the transition client-side (mirrors the backend) so an illegal drag
    // is rejected up-front instead of optimistically moving then snapping back.
    if (!(TRANSITIONS[card.status] ?? []).includes(status)) {
      message.warning(`Can't move ${STATUS_LABEL[card.status] ?? card.status} → ${STATUS_LABEL[status] ?? status} directly`);
      return;
    }
    move.mutate({ id, status });
  };

  const hasFilters = !!(search || severity || status || category);
  const clearFilters = () => { setSearch(''); setSeverity(''); setStatus(''); setCategory(''); };

  // Unified list/table view — scalable triage for high volume. Status changes
  // via an inline dropdown (gated) instead of drag; the backend validates the
  // transition just as the board's drag does.
  const listColumns: any[] = [
    { title: 'No.', dataIndex: 'complaintNo', width: 150, render: (v: string) => <b>{v}</b>,
      sorter: (a: any, b: any) => (a.complaintNo ?? '').localeCompare(b.complaintNo ?? '') },
    { title: 'Severity', dataIndex: 'severity', width: 110,
      sorter: (a: any, b: any) => (SEV_ORDER[a.severity] ?? 0) - (SEV_ORDER[b.severity] ?? 0),
      render: (s: string) => <Tag tone={SEVERITY_COLOR[s]}>{s}</Tag> },
    { title: 'Category', dataIndex: 'category', width: 150, render: (v: string) => (v ? <Tag>{v}</Tag> : '—') },
    {
      title: 'Customer', width: 200, ellipsis: true,
      render: (_: any, r: any) => (r.party ? (
        <div>
          <span style={{ fontWeight: 500 }}>{r.party.name}</span>{' '}
          <Tag tone={r.party.type === 'RETAILER' ? 'blue' : 'green'} style={{ marginInlineEnd: 0 }}>
            {r.party.type === 'RETAILER' ? 'Retailer' : 'Farmer'}
          </Tag>
          {r.party.location ? <div className="text-xs" style={{ color: '#8c8c8c' }}>{r.party.location}</div> : null}
        </div>
      ) : <span style={{ color: '#bbb' }}>—</span>),
    },
    { title: 'Description', dataIndex: 'description', ellipsis: true },
    { title: 'Status', dataIndex: 'status', width: 170,
      filters: STATUS_OPTIONS.map((s) => ({ text: s.label, value: s.value })),
      onFilter: (val: any, r: any) => r.status === val,
      render: (s: string, r: any) => (canManage ? (
        // Wrap so interacting with the dropdown never toggles the row's expand
        // (expandRowByClick) — stop BOTH mousedown (when AntD opens the Select) and click.
        <span onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
          <Select
            size="small" value={s} style={{ width: 150 }}
            loading={move.isPending && move.variables?.id === r.id}
            options={[s, ...(TRANSITIONS[s] ?? [])].map((v) => ({ value: v, label: STATUS_LABEL[v] ?? v }))}
            onChange={(val) => { if (val !== s) move.mutate({ id: r.id, status: val }); }}
          />
        </span>
      ) : <Tag tone={STATUS_TAG[s]}>{STATUS_LABEL[s] ?? s}</Tag>) },
    { title: 'SLA', dataIndex: 'slaDueAt', width: 180, defaultSortOrder: 'ascend' as const,
      sorter: (a: any, b: any) => bySla(a, b),
      render: (v: string, r: any) => {
        if (!v) return '—';
        const breach = new Date(v) < new Date() && !['RESOLVED', 'CLOSED'].includes(r.status);
        return (
          <span style={{ color: breach ? '#D9363E' : undefined }}>
            {breach && <AlertCircle size={16} style={{ marginRight: 4 }} />}
            {formatIstDate(v)} {formatIstTime(v)}
          </span>
        );
      } },
    { title: 'Raised', dataIndex: 'createdAt', width: 120, render: (v: string) => formatIstDate(v),
      sorter: (a: any, b: any) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime() },
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>Complaints</Typography.Title>
        <Space>
          <Segmented
            value={view}
            onChange={(v) => setView(v as 'board' | 'list')}
            options={[
              { label: 'Board', value: 'board', icon: <LayoutGrid size={16} /> },
              { label: 'List', value: 'list', icon: <List size={16} /> },
            ]}
          />
          {canCreate && (
            <Button variant="primary" icon={<Plus size={16} />} onClick={() => setFormOpen(true)}>
              Raise Complaint
            </Button>
          )}
        </Space>
      </div>

      <Card className="mb-4" density="compact">
        <Space wrap size={8}>
          <Input
            allowClear
            prefix={<Search size={16} />}
            placeholder="Search no, description, category"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 260 }}
          />
          <Select
            style={{ width: 150 }}
            value={severity}
            onChange={setSeverity}
            options={[{ value: '', label: 'All severities' }, ...SEVERITY_OPTIONS]}
          />
          <Select
            style={{ width: 160 }}
            value={status}
            onChange={setStatus}
            options={[{ value: '', label: 'All statuses' }, ...STATUS_OPTIONS]}
          />
          <Select
            showSearch
            optionFilterProp="label"
            style={{ width: 200 }}
            value={category}
            onChange={setCategory}
            options={[{ value: '', label: 'All categories' }, ...categories.map((c) => ({ value: c, label: c }))]}
          />
          {hasFilters && <Button variant="text" onClick={clearFilters} style={{ paddingInline: 4 }}>Clear</Button>}
          <Tooltip label="Refresh">
            <Button aria-label="Refresh" icon={<RefreshCw size={16} />} loading={isFetching} onClick={() => refetch()} />
          </Tooltip>
          <Typography.Text type="secondary" className="text-xs">
            {view === 'board'
              ? (canManage ? 'Drag a card between columns to change status.' : 'Status is managed by the support team.')
              : `Click a row to expand details${canManage ? ' · change status from the Status column' : ''}.`}
          </Typography.Text>
        </Space>
      </Card>

      {view === 'board' && (
      <>
      {/* Active workflow board */}
      <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 6, marginBottom: 20, alignItems: 'flex-start' }}>
        {COLUMNS.map((col) => {
          const items = (grouped[col.key] ?? []).slice().sort(bySla);
          const isHover = hoverCol === col.key;
          return (
            <div
              key={col.key}
              onDragOver={(e) => { e.preventDefault(); if (hoverCol !== col.key) setHoverCol(col.key); }}
              onDragLeave={() => setHoverCol((h) => (h === col.key ? null : h))}
              onDrop={(e) => onDrop(e, col.key)}
              style={{
                flex: '1 1 0',
                minWidth: 232,
                display: 'flex',
                flexDirection: 'column',
                maxHeight: 'calc(100vh - 280px)',
                background: isHover ? col.tone : token.colorFillQuaternary,
                border: `1px solid ${isHover ? col.color : token.colorBorderSecondary}`,
                borderRadius: 10,
                padding: 10,
                transition: 'background 120ms ease, border-color 120ms ease',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '2px 4px 12px' }}>
                <Space size={8}>
                  <span style={{ width: 9, height: 9, borderRadius: 5, background: col.color, display: 'inline-block' }} />
                  <b>{col.title}</b>
                </Space>
                <Badge count={items.length} showZero color={col.color} />
              </div>

              <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, marginInline: -2, paddingInline: 2 }}>
              {items.length === 0 ? (
                <div style={{ paddingTop: 28 }}>
                  <EmptyState title={isHover ? 'Drop here' : 'No items'} />
                </div>
              ) : (
                items.map((c) => {
                  const breach = c.slaDueAt && new Date(c.slaDueAt) < new Date() && !['RESOLVED', 'CLOSED'].includes(c.status);
                  return (
                    <div
                      key={c.id}
                      draggable={canManage}
                      onDragStart={canManage ? (e) => onDragStart(e, c.id) : undefined}
                      onDragEnd={() => { setDraggingId(null); setHoverCol(null); }}
                      onClick={() => navigate(`/complaints/${c.id}`)}
                      className="rounded-lg border border-line bg-surface"
                      style={{
                        marginBottom: 8,
                        cursor: canManage ? 'grab' : 'pointer',
                        opacity: draggingId === c.id ? 0.4 : 1,
                        borderLeft: `4px solid ${col.color}`,
                        padding: 10,
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <b className="text-sm">{c.complaintNo}</b>
                        {breach && (
                          <Tooltip label="SLA breached">
                            <AlertCircle size={16} style={{ color: '#D9363E' }} />
                          </Tooltip>
                        )}
                      </div>
                      <Space size={4} wrap style={{ marginTop: 6 }}>
                        <Tag tone={SEVERITY_COLOR[c.severity]} style={{ marginRight: 0 }}>{c.severity}</Tag>
                        {c.category && <Tag style={{ marginRight: 0 }}>{c.category}</Tag>}
                      </Space>
                      {c.description && (
                        <div className="text-xs" style={{ marginTop: 8, color: token.colorTextSecondary, lineHeight: 1.4 }}>
                          {(c.description ?? '').substring(0, 90)}{c.description.length > 90 ? '…' : ''}
                        </div>
                      )}
                      {c.slaDueAt && (
                        <div className="text-2xs" style={{ color: breach ? '#D9363E' : token.colorTextTertiary, marginTop: 8 }}>
                          <Clock size={16} /> SLA {formatIstDate(c.slaDueAt)} {formatIstTime(c.slaDueAt)}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Closed complaints — table */}
      <Card
        title={
          <Space>
            <span style={{ width: 9, height: 9, borderRadius: 5, background: '#8C8C8C', display: 'inline-block' }} />
            <span>Closed Complaints</span>
            <Badge count={closedRows.length} showZero color="#8C8C8C" />
          </Space>
        }
      >
        <Table
          rowKey="id"
          size="small"
          loading={isFetching}
          dataSource={closedRows}
          pagination={{ defaultPageSize: 10, showSizeChanger: true, hideOnSinglePage: true }}
          onRow={(r) => ({ onClick: () => navigate(`/complaints/${r.id}`), style: { cursor: 'pointer' } })}
          locale={{ emptyText: <EmptyState title="No closed complaints" /> }}
          columns={[
            { title: 'Complaint No', dataIndex: 'complaintNo', width: 150 },
            {
              title: 'Severity', dataIndex: 'severity', width: 110,
              render: (s: string) => <Tag tone={SEVERITY_COLOR[s]}>{s}</Tag>,
            },
            {
              title: 'Category', dataIndex: 'category', width: 160,
              render: (v: string) => (v ? <Tag>{v}</Tag> : '—'),
            },
            { title: 'Description', dataIndex: 'description', ellipsis: true },
            {
              title: 'Raised', dataIndex: 'createdAt', width: 130,
              render: (v: string) => formatIstDate(v),
            },
            {
              title: 'Resolved', dataIndex: 'resolvedAt', width: 160,
              render: (v: string) => (v ? formatIst(v) : '—'),
            },
          ]}
        />
      </Card>
      </>
      )}

      {view === 'list' && (
        <Card density="compact">
          <Table
            rowKey="id"
            size="small"
            loading={isFetching}
            dataSource={listRows}
            pagination={{ defaultPageSize: 20, showSizeChanger: true, showTotal: (t) => `${t} complaints` }}
            locale={{ emptyText: <EmptyState title="No complaints match your filters" /> }}
            columns={listColumns}
            onRow={(r: any) => ({ style: { background: STATUS_ROW_TONE[r.status] } })}
            expandable={{
              expandRowByClick: true,
              expandedRowRender: (r: any) => {
                const breach = r.slaDueAt && new Date(r.slaDueAt) < new Date() && !['RESOLVED', 'CLOSED'].includes(r.status);
                return (
                  <Descriptions size="small" bordered column={3} style={{ margin: 0 }}>
                    <Descriptions.Item label="Description" span={3}>{r.description || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Raised by" span={2}>
                      {r.party ? (
                        <>
                          <Tag tone={r.party.type === 'RETAILER' ? 'blue' : 'green'}>
                            {r.party.type === 'RETAILER' ? 'Retailer' : 'Farmer'}
                          </Tag>
                          <b>{r.party.name}</b>{r.party.mobile ? ` · ${r.party.mobile}` : ''}
                        </>
                      ) : '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Location">
                      {r.party?.location || '—'}
                      {r.party?.latitude && r.party?.longitude ? (
                        <>
                          {r.party.location ? ' · ' : ''}
                          <a
                            href={`https://www.google.com/maps?q=${r.party.latitude},${r.party.longitude}`}
                            target="_blank" rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                          >View on map ↗</a>
                        </>
                      ) : null}
                    </Descriptions.Item>
                    <Descriptions.Item label="Status"><Tag tone={STATUS_TAG[r.status]}>{STATUS_LABEL[r.status] ?? r.status}</Tag></Descriptions.Item>
                    <Descriptions.Item label="Severity"><Tag tone={SEVERITY_COLOR[r.severity]}>{r.severity}</Tag></Descriptions.Item>
                    <Descriptions.Item label="Category">{r.category || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Source">{r.source || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Batch">{r.batchNo || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Attachments">{(r.attachments?.length ?? 0)} file{(r.attachments?.length ?? 0) === 1 ? '' : 's'}</Descriptions.Item>
                    <Descriptions.Item label="SLA due">
                      <span style={{ color: breach ? '#D9363E' : undefined }}>
                        {r.slaDueAt ? `${formatIstDate(r.slaDueAt)} ${formatIstTime(r.slaDueAt)}` : '—'}{breach ? ' · breached' : ''}
                      </span>
                    </Descriptions.Item>
                    <Descriptions.Item label="Raised">{formatIst(r.createdAt)}</Descriptions.Item>
                    <Descriptions.Item label="Resolved">{r.resolvedAt ? formatIst(r.resolvedAt) : '—'}</Descriptions.Item>
                    {r.resolutionNotes && <Descriptions.Item label="Resolution" span={3}>{r.resolutionNotes}</Descriptions.Item>}
                    <Descriptions.Item label=" " span={3}>
                      <a onClick={(e) => { e.stopPropagation(); navigate(`/complaints/${r.id}`); }}>Open full detail →</a>
                    </Descriptions.Item>
                  </Descriptions>
                );
              },
            }}
          />
        </Card>
      )}

      <ComplaintFormModal open={formOpen} onClose={() => setFormOpen(false)} />
    </div>
  );
}
